import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * Deferred close so the pointer can land on the mega menu after leaving the top-level row.
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #deactivateTimer = null;

  /** @type {number} */
  #pointerX = NaN;
  /** @type {number} */
  #pointerY = NaN;
  #trackingPointer = false;

  /** @param {PointerEvent} e */
  #onPointerMove = (e) => {
    this.#pointerX = e.clientX;
    this.#pointerY = e.clientY;
  };

  #startTrackingPointer() {
    if (!this.#trackingPointer) {
      document.addEventListener('pointermove', this.#onPointerMove);
      this.#trackingPointer = true;
    }
  }

  #stopTrackingPointer() {
    if (this.#trackingPointer) {
      document.removeEventListener('pointermove', this.#onPointerMove);
      this.#trackingPointer = false;
    }
  }

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#deactivate());
    this.#cacheMaxOverflowMenuHeight();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    // Non-passive so we can hand the gesture off to the page at the scroll boundary.
    this.addEventListener('wheel', this.#onWheel, {passive: false});
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.removeEventListener('wheel', this.#onWheel);
    this.#stopTrackingPointer();
    this.#cleanupMutationObserver();
    if (this.#deactivateTimer) {
      clearTimeout(this.#deactivateTimer);
      this.#deactivateTimer = null;
    }
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    this.#cacheMaxOverflowMenuHeight();
    setHeaderMenuStyle();
    if (this.#state.activeItem && this.ariaExpanded === 'true') {
      const submenu =
        findSubmenu(this.#state.activeItem) ?? (this.overflowMenu instanceof HTMLElement ? this.overflowMenu : null);
      if (submenu) this.#applySubmenuViewportMetrics(submenu);
    }
  }, 100);

  /** Desktop mega menu: panel fills from its top edge to the bottom of the viewport. */
  #isDesktopMegaMenu() {
    return window.matchMedia('(min-width: 990px)').matches;
  }

  /**
   * @param {HTMLElement} submenu
   * @returns {number}
   */
  #computeSubmenuPanelHeight(submenu) {
    const contentHeight = submenu.offsetHeight;
    if (!this.#isDesktopMegaMenu()) return contentHeight;
    const top = submenu.getBoundingClientRect().top;
    const fillHeight = Math.ceil(window.innerHeight - top);
    return Math.max(contentHeight, fillHeight);
  }

  /**
   * Scrollable region uses remaining viewport below the inner wrapper (desktop only).
   * @param {HTMLElement} submenu
   */
  #syncSubmenuScrollMax(submenu) {
    const hc = this.headerComponent;
    if (!hc) return;
    if (!this.#isDesktopMegaMenu()) {
      hc.style.removeProperty('--submenu-scroll-max');
      return;
    }
    const inner =
      submenu.querySelector('.menu-list__submenu-inner') ||
      submenu.querySelector('[part="overflow-list"]');
    if (!(inner instanceof HTMLElement)) {
      hc.style.removeProperty('--submenu-scroll-max');
      return;
    }
    const top = inner.getBoundingClientRect().top;
    const maxPx = Math.max(120, Math.ceil(window.innerHeight - top));
    hc.style.setProperty('--submenu-scroll-max', `${maxPx}px`);
  }

  /**
   * @param {HTMLElement} submenu
   */
  #applySubmenuViewportMetrics(submenu) {
    const heightPx = this.#computeSubmenuPanelHeight(submenu);
    this.headerComponent?.style.setProperty('--submenu-height', `${heightPx}px`);
    this.#syncSubmenuScrollMax(submenu);
  }

  /**
   * The scrollable inner region of the currently active submenu, if any.
   * @returns {HTMLElement | null}
   */
  #activeScroller() {
    const item = this.#state.activeItem;
    if (!item) return null;
    const submenu = findSubmenu(item) ?? (this.overflowMenu instanceof HTMLElement ? this.overflowMenu : null);
    if (!submenu) return null;
    const inner =
      submenu.querySelector('.menu-list__submenu-inner') ||
      submenu.querySelector('[part="overflow-list"]');
    return inner instanceof HTMLElement ? inner : null;
  }

  /**
   * Continuous scroll hand-off: while the desktop mega menu is open, scrolling
   * down inside the panel scrolls its options; the moment that scroll reaches
   * the bottom, the same gesture closes the menu and the leftover motion is
   * passed to the page — instead of the gesture latching to the panel and
   * forcing a second scroll to escape.
   *
   * @param {WheelEvent} event
   */
  #onWheel = (event) => {
    if (event.deltaY <= 0) return;
    if (this.ariaExpanded !== 'true' || !this.#state.activeItem) return;
    if (!this.#isDesktopMegaMenu()) return;

    const scroller = this.#activeScroller();
    if (!scroller) return;

    // Normalise line/page deltas to pixels so the boundary maths is consistent.
    let delta = event.deltaY;
    if (event.deltaMode === 1) delta *= 16;
    else if (event.deltaMode === 2) delta *= scroller.clientHeight;

    const remaining = scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop;

    // Still room to scroll within the menu: let the native scroll run.
    if (remaining > delta) return;

    // Boundary crossed in this gesture — finish the menu scroll, close the
    // menu, and continue the motion on the page in one uninterrupted stroke.
    event.preventDefault();
    if (remaining > 0) scroller.scrollTop = scroller.scrollHeight;

    const overshoot = delta - Math.max(remaining, 0);
    this.#deactivate(this.#state.activeItem, true);

    const doc = document.scrollingElement;
    if (doc && overshoot > 0) doc.scrollTop += overshoot;
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (this.#deactivateTimer) {
      clearTimeout(this.#deactivateTimer);
      this.#deactivateTimer = null;
    }
    this.#stopTrackingPointer();

    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.#applySubmenuViewportMetrics(submenu);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    if (submenu) {
      this.#applySubmenuViewportMetrics(submenu);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.#applySubmenuViewportMetrics(submenu));
      });
    } else {
      this.headerComponent?.style.setProperty('--submenu-height', '0px');
    }

    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay.
   *
   * For pointer-leave: tracks the pointer's real-time position during a 100 ms
   * grace window so a slow diagonal move from the nav link into the mega-menu
   * panel is detected correctly (the old setTimeout-0 approach used stale
   * leave-coordinates and closed too early).
   *
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    if (this.#deactivateTimer) {
      clearTimeout(this.#deactivateTimer);
      this.#deactivateTimer = null;
    }

    if (event.type === 'pointerleave' && event instanceof PointerEvent) {
      this.#pointerX = event.clientX;
      this.#pointerY = event.clientY;
      this.#startTrackingPointer();

      this.#deactivateTimer = setTimeout(() => {
        this.#deactivateTimer = null;
        this.#stopTrackingPointer();
        this.#evaluateDeactivation();
      }, 100);
    } else {
      this.#evaluateDeactivation();
    }
  }

  /**
   * Decide whether to close the mega-menu based on the pointer's current
   * position.  Uses a bounding-rect "safe zone" (nav-item top → submenu
   * bottom, full submenu width) so grid/flex gaps inside the panel don't
   * cause a false close, then falls back to elementsFromPoint and :hover.
   */
  #evaluateDeactivation() {
    const item = this.#state.activeItem;
    if (!item) return;

    const submenu = findSubmenu(item);
    const listItem = item.closest('.menu-list__list-item');

    if (document.activeElement instanceof Node && submenu?.contains(document.activeElement)) return;

    const x = this.#pointerX;
    const y = this.#pointerY;
    const hasCoords = !Number.isNaN(x) && !Number.isNaN(y);

    if (hasCoords) {
      // Safe-zone: a rectangle from the list-item's top edge to the
      // submenu's bottom edge, spanning the submenu's full width.  Keeps the
      // menu open while the pointer is anywhere in that band (covers the gap
      // between the nav row and the dropdown, plus grid gaps inside it).
      if (submenu) {
        const subRect = submenu.getBoundingClientRect();
        const topBound = listItem
          ? Math.min(listItem.getBoundingClientRect().top, subRect.top)
          : subRect.top;

        if (x >= subRect.left && x <= subRect.right && y >= topBound && y <= subRect.bottom) return;
      }

      // elementsFromPoint for overflow-slot / shadow-DOM edge cases
      const stack = document.elementsFromPoint(x, y);
      for (const node of stack) {
        if (!(node instanceof Element)) continue;
        if (submenu && isComposedDescendantOf(submenu, node)) return;
        if (listItem && isComposedDescendantOf(listItem, node)) return;
        if (node.closest('[slot="overflow"]') && this.contains(node)) return;
      }
    }

    if (listItem?.matches(':hover') || submenu?.matches(':hover') || this.overflowHovered) return;

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {boolean} [force] - Close even while the overflow panel is hovered (used by the scroll hand-off).
   */
  #deactivate = (item = this.#state.activeItem, force = false) => {
    if (!item || item != this.#state.activeItem) return;
    if (!force && this.overflowHovered) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.headerComponent?.style.removeProperty('--submenu-scroll-max');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Caches the maximum height of all submenus for consistent animations
   * Stores the value in a CSS custom property for use in transitions
   */
  #cacheMaxOverflowMenuHeight() {
    const submenus = this.querySelectorAll('[ref="submenu[]"]');
    const maxHeight = Math.max(
      ...Array.from(submenus)
        .filter((submenu) => submenu instanceof HTMLElement)
        .map((submenu) => submenu.offsetHeight)
    );
    this.headerComponent?.style.setProperty('--submenu-max-height', `${maxHeight}px`);
  }

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * Whether `node` is `ancestor` or a descendant in the composed tree (light DOM + shadow roots).
 * @param {Node | null | undefined} ancestor
 * @param {Node | null | undefined} node
 * @returns {boolean}
 */
function isComposedDescendantOf(ancestor, node) {
  if (!ancestor || !node) return false;
  if (ancestor.contains(node)) return true;

  let current = node;
  while (current) {
    if (current === ancestor) return true;
    const parent = current.parentNode;
    if (!parent) return false;
    current = parent instanceof ShadowRoot ? parent.host : parent;
  }
  return false;
}
